import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from 'react-google-autocomplete';
import Map, { MapLayerMouseEvent, MapRef, Marker, NavigationControl } from 'react-map-gl';
import { useSelector } from 'react-redux';
import styles from './AddressMap.module.scss';
import {IGeocodingAddress} from "../../../../model";
import {selectAppSettings} from "../../../../features";
import {useAppSelector} from "../../../../store/hooks";
import {geocodingGoogleService} from "../../../../services";

type Props = {
  edit?: boolean;
  className?: string;
  position?: [number, number];
  zoom?: number;
  onPosChange?: (event: { position: [number, number]; zoom: number; address: IGeocodingAddress }) => void;
};

interface IViewState {
  longitude: number;
  latitude: number;
  zoom: number;
}

const ZOOM = 16;

export const AddressMap: FC<Props> = ({ edit, className, position, zoom, onPosChange }) => {
  const settings = useAppSelector(selectAppSettings);
  const [viewState, setViewState] = useState<IViewState>({
    longitude: -100.445882,
    latitude: 39.7837304,
    zoom: 3,
  });
  const mapRef = useRef<MapRef>(null);
  const autocompleteRef = useRef<HTMLInputElement>(null);

  const appSettings = useSelector(selectAppSettings);

  useEffect(() => {
    if (position && zoom) {
      mapRef.current?.flyTo({ center: position, zoom: zoom });
    }
  }, [position, zoom]);

  const dblclickHandler = useCallback(
    (e: MapLayerMouseEvent) => {
      if (onPosChange) {
        geocodingGoogleService.reverse([e.lngLat.lng, e.lngLat.lat]).then((x) => {
          if (x.results?.length) {
            const rawResult = x.results[0];
            if (autocompleteRef && autocompleteRef.current && rawResult.formatted_address) {
              autocompleteRef.current.value = rawResult.formatted_address;
            }

            const parsedAddress = geocodingGoogleService.parseAddress(rawResult);
            onPosChange({
              position: [e.lngLat.lng, e.lngLat.lat],
              zoom: viewState.zoom < ZOOM ? ZOOM : viewState.zoom,
              address: parsedAddress,
            });
          }
        });
      }
    },
    [onPosChange, viewState.zoom],
  );

  useEffect(() => {
    if (!mapRef.current || !edit) {
      return;
    }

    mapRef.current.on('dblclick', dblclickHandler);
    return mapRef.current
      ? () => {
          mapRef.current?.off('dblclick', dblclickHandler);
        }
      : undefined;
  }, [dblclickHandler, edit, mapRef]);

  const onAddressSelected = useCallback(
    (address: unknown) => {
      const parsedAddress = geocodingGoogleService.parseAddress(address);

      if (parsedAddress && parsedAddress.lng && parsedAddress.lat && onPosChange) {
        onPosChange({
          position: [parsedAddress.lng, parsedAddress.lat],
          zoom: viewState.zoom < ZOOM ? ZOOM : viewState.zoom,
          address: parsedAddress,
        });
      }
    },
    [onPosChange, viewState.zoom],
  );

  const content = useMemo(
    () => (
      <div className={`${styles.map} ${className}`}>
        {!!edit && onPosChange && (
          <Autocomplete
            ref={autocompleteRef}
            apiKey={appSettings?.map.google_api_token}
            options={{
              types: ['address'],
              fields: ['place_id', 'address_component', 'geometry'],
            }}
            language='en'
            onPlaceSelected={onAddressSelected}
            className={styles.searchBox}
          />
        )}
        <Map
          ref={mapRef}
          {...viewState}
          maxZoom={18}
          mapboxAccessToken={settings?.map.mapbox_api_token}
          mapStyle={settings?.map.style}
          onMove={(e) => setViewState(e.viewState)}
          onZoom={(e) => setViewState(e.viewState)}
          doubleClickZoom={!edit}
        >
          <NavigationControl position='top-right' />

          {position ? <Marker draggable={true} longitude={position[0]} latitude={position[1]} anchor='bottom'></Marker> : null}
        </Map>
      </div>
    ),
    [
      appSettings?.map.google_api_token,
      className,
      edit,
      onAddressSelected,
      position,
      settings?.map.mapbox_api_token,
      settings?.map.style,
      viewState,
    ],
  );

  return content;
};
